import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { Color } from '@tiptap/extension-color';
import { Details, DetailsContent, DetailsSummary } from '@tiptap/extension-details';
import { Emoji, shortcodeToEmoji } from '@tiptap/extension-emoji';
import { Highlight } from '@tiptap/extension-highlight';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Placeholder } from '@tiptap/extension-placeholder';
import { Subscript } from '@tiptap/extension-subscript';
import { Superscript } from '@tiptap/extension-superscript';
import { TextStyle } from '@tiptap/extension-text-style';
import { Typography } from '@tiptap/extension-typography';
import { mergeAttributes, ReactNodeViewRenderer } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import i18next from 'i18next';
import { common, createLowlight } from 'lowlight';
import { CodeBlockNodeView } from './code-block-node-view';
import { buildEmojiSuggestion } from './emoji/suggestion';
import { buildTableExtensions } from './table/table-kit';

const lowlight = createLowlight(common);

const CodeBlockWithNodeView = CodeBlockLowlight.extend({
  addNodeView() {
    return ReactNodeViewRenderer(CodeBlockNodeView);
  },
});

// referrerpolicy="no-referrer" makes the browser omit the Referer so hotlink-protected hosts don't 403.
const ImageWithReferrerPolicy = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      referrerpolicy: { default: 'no-referrer' },
      's3-key': {
        default: null,
        parseHTML: (element) => element.getAttribute('data-s3-key') ?? null,
        renderHTML: (attrs) => (attrs['s3-key'] ? { 'data-s3-key': attrs['s3-key'] } : {}),
      },
    };
  },
  addNodeView() {
    const buildView = this.parent?.();
    if (typeof buildView !== 'function') {
      return null;
    }

    return (props) => {
      const view = buildView(props);
      const container = view.dom;
      container.querySelector('img')?.addEventListener(
        'error',
        () => {
          container.style.visibility = '';
          container.style.pointerEvents = '';
          container.dataset.imageError = i18next.t('image-failed-to-load');
          container.dataset.imageBroken = 'true';
        },
        { once: true }
      );
      return view;
    };
  },
});

// Always render Unicode; skip the upstream img fallback.
const EmojiUnicodeOnly = Emoji.extend({
  renderHTML({ HTMLAttributes, node }) {
    const emojiItem = shortcodeToEmoji(node.attrs.name, this.options.emojis);
    const attributes = mergeAttributes(HTMLAttributes, this.options.HTMLAttributes, { 'data-type': this.name });
    if (emojiItem?.emoji) {
      return ['span', attributes, emojiItem.emoji];
    }
    return ['span', attributes, `:${node.attrs.name}:`];
  },
});

export interface BuildExtensionsOptions {
  placeholder?: string;
  /**
   * Whether the resulting extension list is for an editable editor (true) or the viewer
   * (false). Defaults to true. Affects `Link.openOnClick`: in the editor clicking a link
   * positions the cursor; in the viewer clicks should navigate.
   */
  editable?: boolean;
}

export const buildBaseExtensions = ({ placeholder, editable = true }: BuildExtensionsOptions = {}) => [
  // Disable the StarterKit extensions we replace with custom configs below.
  StarterKit.configure({
    codeBlock: false,
    link: false,
    dropcursor: {
      color: 'var(--color-primary-3)',
      width: 2,
    },
  }),
  Link.extend({ inclusive: false }).configure({
    openOnClick: !editable,
    defaultProtocol: 'https',
    autolink: true,
    linkOnPaste: true,
    HTMLAttributes: {
      rel: 'noopener noreferrer nofollow',
      target: '_blank',
      spellcheck: 'false',
    },
  }),
  ImageWithReferrerPolicy.configure({
    inline: false,
    allowBase64: true,
    resize: {
      enabled: true,
      directions: ['right'],
      minWidth: 50,
      minHeight: 50,
      alwaysPreserveAspectRatio: true,
    },
  }),
  Subscript,
  Superscript,
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
  EmojiUnicodeOnly.configure({
    suggestion: buildEmojiSuggestion(),
  }),
  Typography,
  CodeBlockWithNodeView.configure({ lowlight }),
  Details.configure({ persist: false }),
  DetailsSummary,
  DetailsContent,
  Placeholder.configure({
    placeholder: placeholder ?? '',
    showOnlyWhenEditable: true,
    showOnlyCurrent: false,
  }),
  ...buildTableExtensions(),
];
